import {
    FillType,
    IBackgroundFillInfo,
    IFillProperty,
    IGradFill,
    IImageFill,
    ISolidFill,
    FILL_KEY,
    IPatternFillStyle, GradientType,
} from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { IBaseFill } from '@src/slide/domain/ui-model/element-modeles/property/fill/base';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { TDWOperationType, TDWActionType, TDWModuleType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import StyleHandler from '@src/slide/domain/ui-model/adapter/style-handler';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import set from 'lodash/set';
import { SolidFillProperty } from '@src/slide/domain/ui-model/element-modeles/property/fill/solid-fill';
import { GradualFillProperty } from '@src/slide/domain/ui-model/element-modeles/property/fill/gradual-fill';
import { ImageFillProperty } from '@src/slide/domain/ui-model/element-modeles/property/fill/image-fill';
import { PattFillProperty } from '@src/slide/domain/ui-model/element-modeles/property/fill/patt-fill';
import { advHas } from '@src/slide/infrastructure/module/independent/assert/get-set';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { IMultiplyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/multiply-model.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { copy, deepCopy } from '@src/slide/infrastructure/module/independent/object-operation';
import { IEmptyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/empty-model.interface';
import UpdateAndRemoveLogic from '@src/slide/domain/ui-model/element-modeles/property/fill/update-remove';
import { mergeNoConflictProperty } from '@src/slide/domain/ui-model/element-modeles/property/utils/merge-property';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { HAS_GRP_FILL } from './adjust-group-children-bg';
import { ICTTableCell, ITableDataModel } from '@tencent/slide-sdk-model';
import { TableCellPropertyConverter } from '@src/slide/domain/ui-model/converter/node-property/element/table-cell-property-converter';
import { changeArrAppendToSetOrModify } from '@src/slide/infrastructure/module/independent/object/arr-append-to-set-or-modify';
import { getFrameAfterRotate } from '@src/slide/infrastructure/module/independent/graphic';
import { ICenterFrame } from '@tencent/alloy-graphics';
import { transaction } from '@src/slide/domain/slide-app';
import { GroupRefreshMenu } from '../menu-property/refresh/group';
import ImageQuickLookManager from '@src/slide/infrastructure/module/dependent/image/image-toolkit/image-quick-look-manager';
import { ITDColor, TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';

export const NO_SELECT = 'NO_SELECT';
type IFillModel = IElementBaseModel | ISlideModel | IMultiplyModel | IEmptyModel;

export default class Fill implements IFillProperty {
    private model: IFillModel;
    private selectionService?: ISelectionService;
    /**
     * 多选图形、组合的属性是综合所有子元素而来的，基于性能考虑，加入缓存，只在必要时重新获取更新缓存
     * @private
     */
    private mergePropertyValCache: IBackgroundFillInfo | null | undefined;
    public constructor(model: IFillModel) {
        this.model = model;
        this.mergePropertyValCache = null;
        this.selectionService = this.model.selectionService;
    }

    public getFillType = (): FillType | undefined => {
        const fillInfo = this.getFillInfo();
        if (!fillInfo || fillInfo.grpFill) return undefined;
        return this.getFinalFillType(fillInfo);
    };

    public getShownFillType = (): FillType | undefined => {
        const fillInfo = this.getFillInfo();
        if (!fillInfo) return undefined;
        return this.getFinalFillType(fillInfo);
    };

    public getShownColor(): string | undefined {
        const fillInfo = this.getFillInfo();
        return fillInfo?.color;
    }

    public getShownTdColor(): ITDColor | undefined {
        const fillInfo = this.getFillInfo();
        return fillInfo?.solidFill?.color;
    }

    public getCurrentFill(): IBaseFill | null {
        const fillType = this.getFillType();
        const fillInfo = this.getFillInfo();
        if (!fillType || !fillInfo) return null;
        const FILL_TYPE_MAP = {
            [FillType.SolidFill]: new SolidFillProperty(fillInfo[fillType] as ISolidFill),
            [FillType.GradualFill]: new GradualFillProperty(fillInfo[fillType] as IGradFill),
            [FillType.ImageFill]: new ImageFillProperty(fillInfo[fillType] as IImageFill),
            [FillType.PattFill]: new PattFillProperty(fillInfo[fillType] as IPatternFillStyle),
            [FillType.NoFill]: null,
        };
        return FILL_TYPE_MAP[fillType];
    }

    public updateCurrentFillForCurrentModel(fillInfo: IBackgroundFillInfo): void {
        this.handleFill(this.model, copy(fillInfo, true), false);
    }

    public updateCurrentFill(fillInfo: IBackgroundFillInfo, onlyUIChange?: boolean | undefined): void {
        this.modifyFillInfo(fillInfo);
        transaction(() => {
            let selectedModels: (ISlideModel | IElementBaseModel)[] = [];
            if (this.model.type === UIModelType.EMPTY || this.model.type === UIModelType.SLIDE) {
                const selectedArray = this.selectionService?.selectedSlides.selectedArray || [];
                selectedModels = selectedArray.length > 0 ? [...selectedArray] : [this.model as ISlideModel];
            } else if (this.model.type === UIModelType.MULTIPLY) {
                selectedModels = [...(this.model as IMultiplyModel).selectedModels];
            } else {
                selectedModels = [(this.model as IElementBaseModel)];
            }
            selectedModels?.forEach((model: IElementBaseModel | ISlideModel) => {
                this.handleFill(model, copy(fillInfo, true), onlyUIChange);
            });
        });
    }

    public getFillInfo = (): IBackgroundFillInfo | null | undefined => {
        if (this.model.type === UIModelType.MULTIPLY
            || this.model.type === UIModelType.TABLE) {
            return this.mergePropertyValCache;
        }
        if (!this.model.props) return null;
        const { style } = (this.model as IElementBaseModel).props;
        // 当style.fillBackground不存在时，默认为无填充。
        const fillBackground = style.fillBackground ?? { noFill: 1 };
        this.setFillShownColor(fillBackground);
        // 必定触发mobx监听，无论是否undefined
        advHas(style, 'fillBackground');
        return fillBackground;
    };

    public getFillColor(): ITDColor | undefined {
        const fillType = this.getFillType();
        if (!fillType) return;
        const noop = () => undefined;
        const handlers = {
            [FillType.SolidFill]: () => this.getSolidFillColor(),
            [FillType.GradualFill]: () => this.getGradientFillColor(),
            [FillType.PattFill]: () => this.getPattFillColor(),
            [FillType.ImageFill]: () => this.getImageFillColor(),
            [FillType.NoFill]: noop,
        };
        return handlers[fillType]?.();
    }

    /**
     * 刷新 多选图形|组合 合并后的效果值
     */
    public refreshMergePropertyValCache(): IBackgroundFillInfo | null | undefined {
        if ([UIModelType.MULTIPLY, UIModelType.TABLE].includes(this.model.type as UIModelType)) {
            const mergeModel = this.model as (IGroupModel | IMultiplyModel);
            // 多选时过滤连接线填充属性
            const getPropertyFunc = model => (model.type === UIModelType.CONNECTOR ? {}
                : { [FILL_KEY]: model.getBackgroundFill?.().getFillInfo() });
            const uiModelPropertyObj = mergeNoConflictProperty(mergeModel, getPropertyFunc);
            this.mergePropertyValCache = uiModelPropertyObj[FILL_KEY] || null;
        }
        return this.mergePropertyValCache;
    }

    public getShadeTitleFrame(): ICenterFrame | undefined {
        const { type } = this.model;
        if (!(type === UIModelType.SLIDE || type === UIModelType.MASTER || type === UIModelType.LAYOUT)
            || !this.getFillInfo()?.shadeToTitle) {
            return;
        }
        const [title] = (this.model as ISlideModel).children.filter(model => model.isTitleOrCtrTitlePl);
        if (!title) {
            return;
        }
        const { left, top, height, width } = getFrameAfterRotate(title.props.style, title.props.style.rotate ?? 0);
        return {
            left,
            top,
            width,
            height,
        };
    }

    // 获取预览显示的 color
    private setFillShownColor = (fillBackground: IBackgroundFillInfo | null | undefined) => {
        if (!fillBackground) return;
        let color;
        if ((fillBackground.gradFill as IGradFill)?.gsList?.length > 0) {
            const gList = (fillBackground.gradFill as IGradFill).gsList;
            const point = gList[gList.length - 1];
            color = point?.color?.srgb_color;
        } else if ((fillBackground.solidFill as ISolidFill)?.color) {
            color = fillBackground.solidFill?.color.srgb_color;
        }
        if (color) {
            fillBackground.color = color;
        }
    };

    private updateModelFill(
        model: IElementBaseModel | ISlideModel, fillInfo: IBackgroundFillInfo,
        onlyUIChange?: boolean | undefined,
    ): void {
        const props = { style: { fillBackground: fillInfo } };
        if (onlyUIChange) {
            this.adjustProps(fillInfo);
            StyleHandler.updateUiProps(props, model);
            if (typeof (model as IElementBaseModel).previewChange !== 'undefined') {
                (model as IElementBaseModel).previewChange += 1;
            }
        } else {
            const updateAndRemove = UpdateAndRemoveLogic.convertToUpdateAndRemove(props, model.props);
            if (fillInfo?.image) {
                model.updateAndRemoveProps(updateAndRemove.update, updateAndRemove.remove);
            } else {
                if (updateAndRemove.remove) {
                    model.updateAndRemoveProps(updateAndRemove.update, updateAndRemove.remove);
                } else {
                    model.updateProps(updateAndRemove.update);
                }
            }
            this.handleUpdateFillReport(props);
        }
    }

    /**
     * 针对不同的model，调用不同的更新接口进行更新
     * @param model
     * @param fillInfo
     * @param onlyUIChange
     */
    private handleFill = (model: IFillModel, fillInfo: IBackgroundFillInfo, onlyUIChange?: boolean): void => {
        if (model.type === UIModelType.TABLE) {
            this.handleTableFill(model as ITableModel, fillInfo, onlyUIChange);
        } else if (model.type === UIModelType.GROUP) {
            this.handleGroupFill(model as IGroupModel, fillInfo, onlyUIChange);
        } else {
            this.updateModelFill(model as IElementBaseModel | ISlideModel, fillInfo, onlyUIChange);
        }
    };

    private handleTableFill = (tableModel: ITableModel, fillInfo: IBackgroundFillInfo, onlyChangeUI?: boolean) => {
        transaction(() => {
            if (onlyChangeUI) {
                if (tableModel.selectedCellRanges !== null) {
                    tableModel.selectedCellRanges.forEach((cellRange) => {
                        const shapeModel = tableModel.props
                            .items[cellRange.rowStart][cellRange.colStart] as IShapeModel;
                        shapeModel.parent = tableModel.parent;
                        const fillBackground = copy(fillInfo, true);
                        shapeModel.getBackgroundFill().updateCurrentFill(fillBackground, onlyChangeUI);
                    });
                }
            } else {
                tableModel.uploadEditingCellData(false);
                this.modifyTableFillColor(tableModel, fillInfo, onlyChangeUI);
            }
        });
    };

    private modifyTableFillColor = (tableModel: ITableModel, fillInfo: IBackgroundFillInfo, onlyChangeUI?: boolean) => {
        const updateProps: any[] = [];
        const removeProps: any[] = [];

        tableModel.props.items.forEach((rowInfo, row) => {
            const updateTcList: any[] = [];
            const removeTcList: any[] = [];
            rowInfo.forEach((shape, col) => {
                // 非合并的单元格才处理数据
                const selectedState = !shape.hMerge && !shape.vMerge
                    && tableModel.cellSelectedState(row, col, tableModel.selectedRange);
                if (selectedState) {
                    const fillBackground = copy(fillInfo, true);
                    if (onlyChangeUI) {
                        set(shape, 'props.style.fillBackground', fillBackground);
                    } else {
                        const props = { style: { fillBackground } };
                        const propsObj = UpdateAndRemoveLogic.convertToUpdateAndRemove(props, shape.props);
                        updateTcList.push({ props: propsObj.update });
                        removeTcList.push({ props: propsObj.remove });
                    }
                } else {
                    updateTcList.push(undefined);
                    removeTcList.push(undefined);
                }
            });
            updateProps.push(updateTcList);
            removeProps.push(removeTcList);
        });

        if (!onlyChangeUI) {
            const sdkDataV3 = tableModel.sdkData as unknown as ITableDataModel;
            transaction(() => {
                updateProps.forEach((row, rowIndex) => {
                    row.forEach((col, colIndex) => {
                        if (!col) {
                            return;
                        }
                        const sdkCell: ICTTableCell = {};
                        TableCellPropertyConverter.convertPartialDataFromUiToSdk(col, sdkCell);
                        const range = { startRowIndex: rowIndex, startColIndex: colIndex, rowSpan: 1, colSpan: 1 };
                        if (sdkCell.tcPr) {
                            // 中台默认传递array是append模式，上层纠正为set或者modify模式
                            changeArrAppendToSetOrModify(sdkCell.tcPr);
                            sdkDataV3.api.updateTableCellProperties(sdkCell.tcPr, range);
                        }
                    });
                });
            });
        }
    };

    private handleGroupFill = (groupModel: IGroupModel, fillInfo: IBackgroundFillInfo, onlyUIChange?: boolean) => {
        transaction(() => {
            groupModel.children.forEach((model: IElementBaseModel) => {
                if (onlyUIChange) {
                    const { fillBackground } = model.props.style;
                    let childFillInfo = fillInfo;
                    if (fillBackground?.grpFill && childFillInfo?.gradFill?.pathGradient?.path === GradientType.Shape) {
                        childFillInfo = deepCopy(fillInfo);
                        set(childFillInfo, 'gradFill.pathGradient.path', GradientType.Radial);
                    }
                    model.getBackgroundFill().updateCurrentFill(childFillInfo, onlyUIChange);
                } else {
                    if (!model.props.style.fillBackground?.grpFill) {
                        model.getBackgroundFill().updateCurrentFill({ grpFill: HAS_GRP_FILL }, onlyUIChange);
                    }
                }
            });
            this.updateModelFill(groupModel, fillInfo, onlyUIChange);
            if (!onlyUIChange && groupModel.isRootGroup) {  // 这里要更新root group的itemValues，否则会有跳动
                GroupRefreshMenu.caculateItemValues(groupModel);
            }
        });
    };

    /**
     * 调整 props 参数
     */
    private adjustProps(fillBackground: IBackgroundFillInfo): void {
        if (!fillBackground) return;
        const fillArr = ['solidFill', 'gradFill', 'image'];
        fillArr.forEach((item) => {
            if (!fillBackground[item]) {
                fillBackground[item] = null;
            }
        });
    }

    private handleUpdateFillReport(props: {style: {fillBackground: IBackgroundFillInfo}}): void {
        // 上报
        let action;
        if (props.style.fillBackground?.gradFill) {
            action = TDWActionType.CLK_CHANGE_COLOR;
        } else if (props.style.fillBackground?.image) {
            action = TDWActionType.CLK_STUFF_IMG;
        }
        if (action) {
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.PC_EDIT_FORMAT,
                action,
            });
        }
    }

    private getFinalFillType = (fillInfo: IBackgroundFillInfo | null | undefined): FillType | undefined => {
        const type = Object.keys(FillType).find(key => fillInfo?.[FillType[key]]);
        return type ? FillType[type] : undefined;
    };

    /**
     * 需要对fillInfo的传参做矫正
     */
    private modifyFillInfo = (fillInfo: IBackgroundFillInfo) => {
        if (fillInfo.image) {
            const quickLook = ImageQuickLookManager.getInstance();
            fillInfo.image.src = quickLook.getResourceByUrl(fillInfo.image.src)?.getCdnUrl() || fillInfo.image.src;
        }
    };

    // 纯色填充
    private getSolidFillColor(): ITDColor | undefined {
        const fillInfo = this.getCurrentFill()?.fillInfo;
        const color = (fillInfo as ISolidFill)?.color;
        return color;
    }

    // 渐变填充
    private getGradientFillColor(): ITDColor | undefined {
        const fillInfo = this.getCurrentFill()?.fillInfo;
        const color = (fillInfo as IGradFill)?.gsList?.[0]?.color as ITDColor;
        return color;
    }

    // 图案填充
    private getPattFillColor(): ITDColor | undefined {
        const fillInfo = this.getCurrentFill()?.fillInfo;
        const color = (fillInfo as IPatternFillStyle)?.fgColor?.color as ITDColor;
        return color;
    }

    // 图片填充
    private getImageFillColor(): ITDColor {
        const color = new TDColor({
            rgbaStr: '#ffffff',
        });
        return color;
    }
}
